/* eslint-disable no-shadow */
// @ts-ignore
// eslint-disable-next-line import/no-cycle
import { thankValue, offensiveValue, poorValue } from 'Constants/constants'

import {
  CampaignStatus,
  PaymentMethodEnum,
  PaymentStatusEnum,
} from 'Constants/enums'

type ID = string | number

export enum UserType {
  curator = 'curator',
}

export interface Notification {
  notice: (arg0: {
    content: JSX.Element
    position: string
    duration: number
    closable: boolean
    style: { top: number; left: number }
    className: string
  }) => void
}

export interface BluesnapCreditCard {
  ccType: string
  last4Digits: string
  exp: string
  issuingCountry: string
  isRegulatedCard: boolean
  cardSubType: string
  binCategory: string
  ccBin: string
  cardType: string
  cardLastFourDigits: string
}

export interface StripePayment {
  amount: number
  campaignId: number
  campaignValue: number
  startDate: string
  couponId: number | null
}

export interface BluesnapCallbackData {
  cardData: BluesnapCreditCard
  threeDSecure: {
    authResult: string
  } | null
}

export interface User {
  first_name: string
  last_name: string
  user_photo_url: string
  is_email_confirmed: boolean
  is_first_campaign: string
  email: string
  role: string
  id: ID
  balance: number
  complaints: number
  isDeactivated: boolean
  country: string
  are_terms_accepted: boolean
  gcid: string
  createdAt: string
  created_at: string
  spotify_id: string
  access_token: string
  refresh_token: string
  curatorId?: ID
  is_feed_banner?: boolean
  is_new_feed?: boolean
}

export interface Influencer {
  id: number
  createdAt: string
  updatedAt: string
  email: string
  role: string
  firstName: string
  lastName: string
  userPhotoUrl: string
  isEmailConfirmed: boolean
  lastActivity: string
}

export type Curator = {
  id: number
  email: string
  last_name: string
  first_name: string
  username: string
  user_photo_url: string
  autopay: boolean
  negative_responses: number
  playlist_followers: Array<number>
  followers_count: number
  playlists_count: number
  rating: number
  adding_rate: number
  Payouts: Array<Payout>
  Playlists: Array<Playlist>
  autopay_date: Date
  are_terms_accepted: string
  user_id: number
  spotify_id: string
  User: User
  Recipient: {
    is_checking_needed: boolean
    country: string
  }
}

export type Payout = {
  id: number
  amount: number
  date: string
  status: PayoutStatus
  transfer_id: number | null
  curator_id: number
  curator_complaints: number
  curator_payouts: number
  Curator: Curator
  total_payouts: number
  requests_since_autopayout: number
  autopayout_status: boolean
  autopay: boolean
  Recipient: {
    is_checking_needed: boolean
    country: string
  }
}

export interface SpotifyPlaylist {
  data: {
    followers: {
      total: number
    }
    tracks: {
      total: number
    }
    owner: {
      id: string
    }
  }
}

// export interface SpotifyTrack {
//   id: string
//   name: string
//   href: string
//   duration_ms: number
//   artists: Array<{
//     id: string
//     name: string
//     external_urls: {
//       spotify: string
//     }
//   }>
//   album: {
//     images: Array<{
//       url: string
//     }>
//   }
// }

export interface ReleaseNote {
  title: string
  image_url: string
  text: string
}

// eslint-disable-next-line
export enum PayoutStatus {
  Awaiting = 'awaiting',
  Paid = 'paid',
  Autopayout = 'autopayout',
}

export enum CampaignStatusEnum {
  FIRST = 'first',
  BASIC = 'basic',
  RELAUNCHED = 'relaunched',
}

export enum CampaignTypeEnum {
  PAYMENT_PENDING = 'payment_pending',
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  SCHEDULED = 'scheduled',
  RUNNING = 'running',
  ENDED = 'ended',
  STOPPED = 'stopped',
}

export enum TiktokCampaignStatus {
  AWAITING_REVIEW = 'awaiting_review',
  PENDING = 'pending',
  SCHEDULED = 'scheduled',
  RUNNING = 'running',
  IN_PROCESS = 'in_process',
  ENDED = 'ended',
  CANCELED = 'canceled',
  FINISHED = 'finished',
  STOPPED = 'stopped',
}

export enum CampaignPlatformTypeEnum {
  SPOTIFY = 'spotify',
  TIKTOK = 'tiktok',
}
export interface Genre {
  id?: number | string
  genre_name?: string
  Genre?: Genre
  playlists_count?: number
  placeholder?: string
  christmas?: boolean
  isDisabled?: boolean
  name?: string
  label?: string
  isCheked?: boolean
  value?: string | number
  genre_id?: ID
}

export interface SelectedLanguage {
  value: number
  label: string
}
export interface ProperGenreOrLanguage {
  id: ID
  name: string
  playlistsCount?: number
}
export interface TrackGenre {
  genre_id?: ID
  Genre: {
    id: ID
    genre_name: string
    genre_id: ID
  }
}

export interface TrackLanguage {
  language_id?: ID
  Language: {
    id: ID
    language_name: string
  }
}

export interface Language {
  id?: number
  language_id?: ID
  language_name?: string
  playlists_count?: number
  value?: number
  label?: string
  name?: string
  Language?: {
    id: number
    language_name: string
  }
}

export enum MoodName {
  christmas = 'christmas',
}

export interface Coupon {
  id: number
  discount: number
  value: string
}

export interface Mood {
  id: number
  mood_id: number
  mood_name: MoodName
  Mood: {
    id: number
    mood_name: string
  }
}

export interface TiktokCampaign {
  id: string
  type: CampaignStatusEnum
  trackCoverUrl: string
  trackTitle: string
  trackId: number
  trackAuthorName: string
  startDate: Date
  endDate: Date
  status: TiktokCampaignStatus
  platformType: CampaignPlatformTypeEnum.TIKTOK
  price: number
  genres: Array<Genre>
  languages: Array<Language>
  amount?: number
  isCurrent: boolean
  isDanceRequested?: boolean
  comment?: string
  audience: {
    maxFollowers: number
    maxVideos: number
    minFollowers: number
    minVideos: number
  }
  is_feed?: boolean
}

export interface DetailsTiktok {
  id: number
  createdAt: string
  updatedAt: string
  status: string
  userId: number
  startDate: string
  endDate: string
  totalVideos: number
  totalLikes: number
  totalViews: number
  totalShares: number
  influencersCount: number
  trackTitle: string
  trackPlayUrl: string
  trackCoverUrl: string
  trackAutorName: string
  trackUrl: string
  trackNewVideosCount?: number
  refound?: number
  amount: number
  expectedMinVideos: number
  expectedMaxVideos: number
}

export interface EventsTiktok {
  data?: Array<{
    id: number
    createdAt: string
    updatedAt: string
    status: string
    tiktokCampaignId: number
    videoLink: string
    videoUploadDate: string
    nickname: string
    avatar: string
    rate: number
    uniqueId: string
    followerCount: number
    influencerTiktokProfileLink: string
  }>
  meta?: {
    pageNumber: number
    pageSize: number
    itemCount: number
    pageCount: number
    hasPreviousPage: boolean
    hasNextPage: boolean
  }
}

export interface EventsTiktokStatuses {
  statuses: [
    {
      status: string
      count: number
    }
  ]
}

export interface SpotifyCampaign {
  createdAt: string | number | Date
  id: string
  trackCoverUrl: string
  trackTitle: string
  endDate: Date
  trackAuthorName: string
  platformType: CampaignPlatformTypeEnum.SPOTIFY
  status: CampaignStatusEnum
  type: CampaignTypeEnum
  startDate: Date
  trackId: number
  genres: Array<Genre>
  languages: Array<Language>
  price: number
  relaunched: boolean
  amount?: number
  trackName: string
  artistName: string
  followers: number
  max_followers: number
  playlists: number
  max_playlists: number
  parent_campaign_id: number
  isCurrent: boolean
  isDanceRequested?: boolean
  comment?: string
  curators_count?: number
  isMinPrice?: boolean
  isMaxPrice?: boolean
  audience: {
    followers: number
    curators: number
    maxCurators: number
    maxFollowers: number
    maxPlaylists: number
    minPlaylists: number
    playlists: number
  }
  loading?: boolean
  overrun_budget?: boolean
  reviewed_reviews: number
  all_review: number
  is_feed?: boolean
}

export interface FeedCampaign {
  id: number
  start_date: Date
  end_date: Date
  track_name: string
  track_image: string
  artist_name: string
  track_url: string
  matchedPlaylists: Array<string>
  matchedGenres: Array<string>
}

export interface FeedTrack {
  id: number
  curator_rating: number
  review_feedback: ArtistFeedback
  review_status: ReviewStatus
  potential_reward: string
  rank: number
  matched_percent: number
  campaign: FeedCampaign
}

export interface CreditCard {
  cardLastFourDigits: number
  cardType: string
}

export interface Shopper {
  // paymentSources: {
  //   creditCardInfo: Array<{
  //     creditCard: CreditCard
  //   }>
  // }
  cardHolderFirstName: string
  cardHolderLastName: string
  shopperToken3DS: string
  creditCards: Array<{
    expirationYear: string
    cardLastFourDigits: string
    cardSubType: string
    cardType: string
    expirationMonth: string
    binCategory: string
    cardRegulated: string
    issuingCountryCode: string
  }>
}

export interface Campaign {
  id: ID
  status: CampaignStatus
  start_date: string | Date
  end_date: string | Date
  endDate: string | Date
  startDate: string | Date
  type: CampaignTypeEnum
  Reviews: Array<Review>
  Track: Track
  PaypalPayments: Array<PaypalPayment>
  //  eslint-disable-next-line @typescript-eslint/no-explicit-any
  Balances: Array<any>
  relaunched: boolean
  price?: number
  trackId?: number
  playlists?: number
  max_playlists?: number
  curators_count?: number
  followers?: number
  isCurrent?: boolean
  moods?: Array<number>
  artistName?: string
  trackName?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  category: any
  genres?: Array<Genre>
  languages?: Array<Language>
  created_at: string
  all_review?: number
  reviewed_reviews?: number
  platformType: CampaignPlatformTypeEnum
  reviewedReviews: number
  allReview: number
  trackCoverUrl: string
  trackTitle: string
  trackAuthorName: string
  is_feed?: boolean
}

export interface PaypalPayment {
  amount: number
}

export interface NewPlaylist {
  genres?: Array<Genre>
  selectedGenres?: Array<Genre>
  selectedLanguages?: Array<Language>
  selectedMoods?: Array<Mood>
  relevantGenres?: Array<Genre>
  playListInfo: {
    id?: number | null
    name?: string
    img?: string
  }
  id?: number | null
}

export interface Track {
  album: {
    album_type: string
    artists: Array<Artist>
    available_markets: Array<string>
    external_urls: { spotify: string }
    href: string
    id: string | number
    images: Array<Image>
    name: string
    release_date: string
    release_date_precision: string
    total_tracks: number
    type: string
    uri: string
  }
  duration_m: number
  artists: Array<Artist>
  available_markets: Array<string>
  disc_number: number
  duration_ms: number
  explicit: boolean
  external_ids: { isrc: 'DECE72003523' }
  external_urls: { spotify: string }
  href: string
  id: string | number
  is_local: boolean
  name: string
  popularity: number
  preview_url: string
  track_number: number
  type: string
  uri: string
  track_name: string
  artist_name: string
  track_image: string
  track_url?: string
  track_id?: number
  TrackGenres: Array<Genre>
  TrackLanguages: Array<Language>
  TrackMoods: Array<Mood>
  spotify_id: string
}

export enum CheckoutMobileActiveSlideEnum {
  START = 'start',
  CHOOSE = 'choose',
  TOTAL = 'total',
  CHECKOUT = 'checkout',
}

export enum ActionsForSelectGenresAndLanguagesEnum {
  SELECT_OPTION = 'select-option',
  REMOVE_VALUE = 'remove-value',
  POP_VALUE = 'pop-value',
}

export interface Playlist {
  id: ID
  playlist_name: string
  playlist_url: string
  followers: number
  tracks: number
  discovered_on_artists: number
  discovered_on_updated_at: string | Date
  monthly_listeners: number
  meets_conditions: boolean
  playlist_cover: string
  addition_type: string
  is_blocked: boolean
  is_limit_exceeded: boolean
  score: number
  extra_score: number
  level: number
  is_new: boolean
  status: string
  is_deactivation_new: boolean
  reviews_amount: number
  curator_id: ID
  PlaylistGenres: Array<TrackGenre>
  PlaylistLanguages: Array<TrackLanguage>
  PlaylistMoods: Array<Mood>
  genres: Array<ProperGenreOrLanguage>
  languages: Array<ProperGenreOrLanguage>
  streams_count: number
  listeners_count: number
  ArtistStats: Array<{ streams: number; listeners: number }>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setDeactivationAlertRead: (id: any) => void
  is_hidden: boolean
}

export interface MatchedPlaylist {
  id: ID
  playlist_name: string
  genres: Array<ProperGenreOrLanguage>
  languages: Array<ProperGenreOrLanguage>
}

export interface MatchedPlaylistReview {
  playlist_id: number
  playlist_name: string
}
export interface Review {
  id: ID
  curator_id: number
  curator_rating: number
  comment: string
  rank: number
  review_date: Date
  review_feedback: ArtistFeedback
  review_refund: number
  playlist_id: ID
  review_status: ReviewStatus
  Curator: Curator
  Playlist?: Playlist
  matched?: Array<MatchedPlaylist>
  potential_reward: string
  rating: number
  reward_received: number
  Campaign: Campaign
  campaign: Campaign
}

export enum ReviewMatchedType {
  Added = 'added',
  Matched = 'matched',
}

export enum PlaylistStatus {
  Activated = 'activated',
  Deactivated = 'deactivated',
  Pending = 'pending',
}

export interface Artist {
  external_urls: { spotify: string }
  href: string
  id: string
  name: string
  type: string
  uri: string
}

interface Image {
  height: number
  url: string
  width: number
}
export interface SpotifyTrack {
  album: {
    album_type: string
    artists: Array<Artist>
    available_markets: Array<string>
    external_urls: { spotify: string }
    href: string
    id: string
    images: Array<Image>
    name: string
    release_date: string
    release_date_precision: string
    total_tracks: number
    type: string
    uri: string
  }
  artists: Array<Artist>
  available_markets: Array<string>
  disc_number: number
  duration_ms: number
  explicit: boolean
  external_ids: { isrc: string }
  external_urls: { spotify: string }
  href: string
  id: string
  is_local: boolean
  name: string
  popularity: number
  preview_url: string | null
  track_number: number
  type: string
  uri: string
}

export interface TikTokTrack {
  initialVideoCount: number
  trackAuthorName: string
  trackCoverUrl: string
  trackDuration: number
  trackPlayUrl: string
  trackTiktokId: string
  trackTitle: string
}

export interface Data {
  email: string
  firstName: string
  lastName: string
  phone_number: string | number
  role: string
}

export enum ReviewStatus {
  awaiting_review = 'awaiting_review',
  reviewed = 'reviewed',
  missed = 'missed',
  cancelled = 'cancelled',
}

export interface ReviewPlaylist {
  id: number
  playlist_name: string
  percent: number
  playlist_cover: string
  score: number
}

export interface ReviewDetails {
  id: number
  campaign_id: number
  comment: string
  curator_rating: number
  potential_reward: string
  rank: number
  reject_reason_id: number
  review_feedback: string
  review_status: ReviewStatus
  playlists: ReviewPlaylist[]
  addedTo?: ReviewPlaylist
  daysLeft: number
  isRejectLastThree: boolean
  track: {
    id: number
    artist_name: string
    genres: ProperGenreOrLanguage[]
    languages: ProperGenreOrLanguage[]
    track_image: string
    track_name: string
    track_url: string
  }
  reward?: number
  stars?: number
  is_feed?: boolean
}

export interface FeedMatchedPlaylist {
  id: number
  playlist_name: string
  playlist_cover: string
  matched_percent: number
  score: number
}

export interface FeedDetailsTrack {
  id: number
  track_name: string
  artist_name: string
  track_url: string
  genres: Array<string>
  languages: Array<string>
}

export interface FeedDetails {
  campaign_id: string
  rank: number
  review_status: ReviewStatus
  matched_playlists: Array<FeedMatchedPlaylist>
  potential_reward: number
  track: FeedDetailsTrack
  review_feedback: ArtistFeedback
  comment: string
  curator_rating: number
  addedTo: FeedMatchedPlaylist
  isRejectLastThree: boolean
  isReserved: boolean
}

export interface TransferwiseRequirement {
  type: string
  title: string
  usageInfo: string
  fields: Array<TransferwiseRequirementField>
}

export interface TransferwiseRequirementField {
  name: string
  group: Array<{
    example: string
    key: string
    name: string
    refreshRequirementsOnChange: boolean
    required: boolean
    valuesAllowed: boolean
    type: 'radio' | 'select' | 'date' | 'text'
  }>
  valuesAllowed: Array<{
    key: string
    name: string
  }>
}

export interface Recipient {
  accountHolderName: string
  currency: string
  type: string
  details: {
    address: RecipientAddress
  }
}

export interface RecipientAddress {
  country: string
  countryCode: string
  firstLine: string
  postCode: string
  city: string
  state: string
}
export interface BluesnapPaymentData {
  cardData: {
    binCategory: string
    last4Digits: string
    ccType: string
  }
}

export interface CampaignItem {
  endDate: string
  id: number | string
  platformType: string
  startDate: string
  status: string
  trackAuthorName: string
  trackCoverUrl: string
  trackTitle: string
  type: string
}

export interface Error {
  error: {
    message: string
  }
  statusCode: number
}

export interface ErrorWithFlag {
  errorMsg: Array<string>
  updated: boolean
}

export enum InfluencerRegisterEnum {
  JOIN = 'join',
  CONNECT = 'connect',
  CONNECTED = 'connected',
  FORM = 'form',
  CONFIRMATION = 'confirmation',
  COMPLETED = 'completed',
  ERROR = 'error_connect',
}

export interface SelectedGenre {
  label: string
  playlistsCount: number
  value: number
}

export enum ReviewType {
  add = 'add',
  reject = 'reject',
}

export interface ReviewSelectValue {
  type: ReviewType
  id: number
}

export enum FeedFilterType {
  date = 'date',
  genres = 'genres',
  playlists = 'playlists',
}

export enum FeedFilterDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface UsersMe {
  id: number
  createdAt: string
  updatedAt: string
  email: string
  role: string
  firstName: string
  userPhotoUrl: string
  isEmailConfirmed: boolean
  lastActivity: string
  spotifyId: string
  metaData: unknown
}

export interface Token {
  token: string
}

export enum FlowType {
  create = 'create_campaign',
  scheduling = 'scheduling_campaign',
}

export interface CampaignToSchedule {
  id: string
  status: CampaignTypeEnum
  type: CampaignStatusEnum
  genres: ProperGenreOrLanguage[]
  languages: ProperGenreOrLanguage[]
  start_date: Date | string
  moods?: Array<Mood>
  playlistsCount?: number
  curatorsCount?: number
  minPlaylistsCount?: number
  maxPlaylistsCount?: number
  minCuratorsCount?: number
  maxCuratorsCount?: number
  is_feed: boolean
  price: number
  track: {
    title: string
    author: string
    cover: string
  }
}

export interface Currency {
  name: string
  rate: number
}

export interface BudgetRange {
  min: number
  max: number
}

export type ArtistFeedback = 'thank' | 'offensive' | 'poor'

export interface GetGtmInfoInterface {
  campaignType: string
  campaignId: number
  genresList: [string]
  languagesList: [string]
  transactionId: string
  value: number
  resultPrice: number
  campaignNumber: number
  purchaseType: string
  isFeed?: boolean
}

export interface RelaunchedCampaign {
  id: number
  status: string
  start_date: null
  end_date: null
  type: string
  Track: {
    artist_name: string
    artist_id: number
    track_name: string
    track_image: string
    track_id: number
    track_length: string
    TrackGenres: [
      {
        id: number
        genre_id: number
        track_id: number
        Genre: { id: number; genre_name: string }
      }
    ]
    TrackLanguages: [
      {
        id: number
        language_id: number
        Language: { id: number; language_name: string }
      }
    ]
  }
  parent_campaign_id: number
  is_feed: boolean
}

export interface CampaignDetailsData {
  Reviews: Array<Review>
  Track: CampaignTrack
  budget: number
  curators_count: number
  curators_count_max?: number
  listeners_count: number
  streams_count: number
  end_date: Date
  id: number
  playlists_adds: number
  playlists_count: number
  playlists_count_max?: number
  playlists_followers_result: number
  start_date: Date
  status: CampaignStatus
  refund: number
  is_sfa_connected: boolean
  reviewed_count: number
  reviews_count: number
  missed_cound: number
  artist_spotify_id: string
  is_feed?: boolean
  type?: string
}

export interface CampaignTrack
  extends Omit<Track, 'TrackGenres' | 'TrackLanguages'> {
  spotify_popularity: number
  spotify_popularity_result: number
  artist_spotify_id: string
  TrackGenres: Array<TrackGenre>
  TrackLanguages: Array<TrackLanguage>
  Artist: {
    user_id: number
    User: {
      first_name: string
      id: number
      last_name: string
      user_photo_url: string
    }
  }
}

export interface Payment {
  balance_used: string
  campaigns_full_price: string
  charged_back_amount: string
  charged_back_at: string
  confirmed_at: string
  created_at: string
  id: number
  paid_amount: string
  status: PaymentStatusEnum
  transaction_id: string
  type: PaymentMethodEnum
  updated_at: string
  user_id: number
}
